import Vue from 'vue'
import VueRouter from 'vue-router'
import store from '@/store'
import { getQueryParam, judgeDevice, isSp, isIos } from '@/util/common'
import * as apiEx from '@/api/ex'
import * as _ from 'lodash'

Vue.use(VueRouter)

// 統合基盤ログイン画面へのリダイレクト
// redirect_url にはワンタイムトークン等のクエリを付けない（再ログイン後の401ループ防止）
const redirectToTechLogin = (path) => {
  const normalizedPath = (path.startsWith('/lc/app/') || path === '/lc/app')
    ? path
    : `/lc/app${path}`
  const fullUrl = `${window.location.origin}${normalizedPath}`
  window.location.replace(`${process.env.VUE_APP_TECH_URL}/login/form?redirect_url=${encodeURIComponent(fullUrl)}`)
}

// ダッシュボードへのリダイレクト
const redirectToDashboard = () => {
  const { common } = store.state
  const device = common.device ?? 'pc'
  window.location.replace(`/lc/app/${device}/dashboard`)
}

// エラー画面へのリダイレクト
// 401（認証エラー）は404ではなくテックポータルのログイン画面へ遷移する
const redirectToError = (errorCode, path = null) => {
  const device = judgeDevice()
  const code = Number(errorCode)

  if (code === 401) {
    const redirectPath = path ||
      store.state.common.toPath ||
      window.location.pathname.replace(/^\/lc\/app/, '') ||
      `/${device}/dashboard`
    redirectToTechLogin(redirectPath)
    return
  }

  if (code === 403) {
    const errorPage = `/lc/app/error/403-${device}.html`
    window.location = errorPage
  } else {
    const errorPage = `/lc/app/error/404-${device}.html`
    window.location = errorPage
  }
}

/**
 * 認証が不要なページは、メタ情報にnoAuthを入れる
 * ユーザー管理者のみがアクセスできるページは、メタ情報にrequireAdminを入れる
 * ページタイトルは、メタ情報のtitleに入れる
 */

const routes = [
  {
    path: '/',
    name: 'root',
    meta: { noAuth: true },
    component: () => import(/* webpackChunkName: "common-base" */ '../views/CommonBase.vue'),
    beforeEnter: async (to, from, next) => {
      if (to.name === 'root') {
        const device = judgeDevice()
        if (device === 'sp') {
          next({ name: 'SPLogin' })
        } else {
          next({ name: 'PCLogin' })
        }
      } else {
        next()
      }
    },
    children: [
      {
        path: 'pc/',
        name: 'PC',
        component: () => import(/* webpackChunkName: "pc-base" */ '../views/pc/PCBase.vue'),
        children: [
          {
            path: 'pc-module',
            name: 'PCModule',
            component: () => import(/* webpackChunkName: "pc-login" */ '../views/PCModule.vue'),
            meta: {
              title: ''
            }
          },
          {
            path: 'login',
            name: 'PCLogin',
            beforeEnter: (to, from, next) => {
              // 301リダイレクト
              redirectToTechLogin('/lc/app/pc/dashboard')
            },
            // component: () => import(/* webpackChunkName: "pc-login" */ '../views/pc/PCLogin.vue'),
            meta: {
              noAuth: true,
              title: 'ログイン'
            }
          },
          {
            path: 'dashboard',
            name: 'PCDashboard',
            component: () => import(/* webpackChunkName: "pc-dashboard" */ '../views/pc/PCDashboard.vue'),
            meta: { title: 'ダッシュボード' },
            beforeEnter: async (to, from, next) => {

              const token = getQueryParam('token')
              const refreshToken = getQueryParam('refresh_token')

              if (token !== '') {
                // クエリ文字列にtokenが設定されている場合
                // フロントエンドログイン / アカウントログインのログイン試行であるとみなす

                // 通常、フロントエンドログイン / アカウントログインをすると、router.beforeEach()の処理に入るが、
                // LCにログイン後、ログアウトせずに、管理システムからフロントエンドログイン / アカウントログインした場合で、
                // セッションが残っているとここに来るため、ここでもログイン認証を行う

                const isAuthorized = await store.dispatch('auth/checkToken', token, refreshToken)
                if (isAuthorized) {
                  // 認証OK

                  // クエリ文字列で渡されたトークンとリフレッシュトークンをストアに保存
                  store.dispatch('auth/setToken', { token: token, refreshToken: refreshToken })

                  // 次の画面へ遷移
                  next()
                } else {
                  // 認証NG
                  store.dispatch('common/showErrorInFullScreen', { title: '認証エラー', text: 'このページを表示する権限がありません。' })
                }
              } else {
                // 通常の遷移
                next()
              }
            }
          },
          {
            path: 'information-list',
            name: 'PCInformationList',
            component: () => import(/* webpackChunkName: "pc-information-list" */ '../views/pc/PCInformationList.vue'),
            meta: { title: 'お知らせ一覧' }
          },
          {
            path: 'information-detail/:infoId',
            name: 'PCInformationDetail',
            component: () => import(/* webpackChunkName: "pc-information-detail" */ '../views/pc/PCInformationDetail.vue'),
            meta: {
              title: ' '
              // title: '★お知らせタイトル★
              // お知らせタイトルは詳細ページ側でつける
            },
            // vue-routerがcomposition-apiに対応していないのでパラメータをpropsで渡す
            props: (route) => {
              return {
                infoId: Number(route.params.infoId)
              }
            }
          },
          {
            path: 'qa-list',
            name: 'PCQAList',
            component: () => import(/* webpackChunkName: "pc-qa-list" */ '../views/pc/PCQAList.vue'),
            meta: { title: 'Q&A・事例検索' },
            beforeEnter: async (to, from, next) => {

              // QA一覧に対する遷移元の名前を保存
              store.dispatch('qa/setFromNameForQAList', from.name)

              if (from.name !== 'PCQADetail') {
                // QA詳細画面以外から遷移してきた場合
                // ページ番号を1に初期化する
                store.dispatch('qa/setPage', 1)
              }
              next()
            }
          },
          {
            path: 'qa-detail/:qaId',
            name: 'PCQADetail',
            component: () => import(/* webpackChunkName: "pc-qa-detail" */ '../views/pc/PCQADetail.vue'),
            meta: {
              // title: '★設問タイトル★-Q&A・事例詳細'
              // 設問タイトルは詳細ページ側でつける
              title: '-Q&A・事例詳細'
            },
            // vue-routerがcomposition-apiに対応していないのでパラメータをpropsで渡す
            props: (route) => {
              return {
                qaId: Number(route.params.qaId)
              }
            }
          },
          // 資料検索
          {
            path: 'document-list',
            name: 'PCDocumentList',
            component: () => import(/* webpackChunkName: "pc-document-list" */ '../views/pc/PCDocumentList.vue'),
            meta: { title: '資料検索' }
          },
          {
            path: 'document-list/:bookRevisionId',
            name: 'PCDocumentListBook',
            component: () => import(/* webpackChunkName: "pc-document-list-book" */ '../views/pc/PCDocumentList.vue'),
            meta: { title: '資料検索' },
            props: (route) => {
              return {
                bookRevisionId: Number(route.params.bookRevisionId)
              }
            }
          },
          {
            path: 'document-list/theme/:themeId',
            name: 'PCDocumentListTheme',
            component: () => import(/* webpackChunkName: "pc-document-list-theme" */ '../views/pc/PCDocumentList.vue'),
            meta: { title: '資料検索' },
            // vue-routerがcomposition-apiに対応していないのでパラメータをpropsで渡す
            props: (route) => {
              return {
                themeId: Number(route.params.themeId)
              }
            }
          },
          {
            path: 'document-list/detail/:detailId',
            name: 'PCDocumentListDetail',
            component: () => import(/* webpackChunkName: "pc-document-list-detail" */ '../views/pc/PCDocumentList.vue'),
            meta: { title: '資料検索' },
            // vue-routerがcomposition-apiに対応していないのでパラメータをpropsで渡す
            props: (route) => {
              return {
                detailId: Number(route.params.detailId)
              }
            }
          },
          {
            path: 'document-list/:bookId/:previewBookRevisionId/',
            name: 'PCDocumentListPreview',
            component: () => import(/* webpackChunkName: "pc-document-list-preview" */ '../views/pc/PCDocumentList.vue'),
            meta: { title: '資料検索' },
            // vue-routerがcomposition-apiに対応していないのでパラメータをpropsで渡す
            props: (route) => {
              return {
                bookId: Number(route.params.bookId),
                previewBookRevisionId: Number(route.params.previewBookRevisionId)
              }
            }
          },
          {
            path: 'document-search',
            name: 'PCDocumentSearch',
            component: () => import(/* webpackChunkName: "pc-document-search" */ '../views/pc/PCDocumentSearch.vue'),
            meta: { title: '資料検索' }
          },
          {
            path: 'document-search/detail/:detailId',
            name: 'PCDocumentSearchDetail',
            component: () => import(/* webpackChunkName: "pc-document-search-detail" */ '../views/pc/PCDocumentSearch.vue'),
            meta: { title: '資料検索' },
            // vue-routerがcomposition-apiに対応していないのでパラメータをpropsで渡す
            props: (route) => {
              return {
                detailId: Number(route.params.detailId)
              }
            }
          },
          // 管理システムの書籍プレビューから遷移してきた場合のURL
          {
            path: 'qa-detail/:bookId/:bookRevisionId/:qaId',
            name: 'PCQADetailPreview',
            component: () => import(/* webpackChunkName: "pc-qa-detail" */ '../views/pc/PCQADetail.vue'),
            meta: {
              // title: '★設問タイトル★-Q&A・事例詳細'
              // 設問タイトルは詳細ページ側でつける
              title: '-Q&A・事例詳細'
            },
            // vue-routerがcomposition-apiに対応していないのでパラメータをpropsで渡す
            props: (route) => {
              return {
                bookId: Number(route.params.bookId),
                bookRevisionId: Number(route.params.bookRevisionId),
                qaId: Number(route.params.qaId)
              }
            }
          },
          // タイトル付きPDFのURL
          {
            path: 'qa-detail-pdf/:qaId',
            name: 'PCQADetailPdf',
            component: () => import(/* webpackChunkName: "pc-qa-detail-pdf" */ '../views/pc/PCQADetailPdf.vue'),
            meta: {
              // title: '★設問タイトル★-Q&A・事例詳細'
              // 設問タイトルは詳細ページ側でつける
              title: '-Q&A・事例詳細PDF'
            },
            // vue-routerがcomposition-apiに対応していないのでパラメータをpropsで渡す
            props: (route) => {
              return {
                qaId: Number(route.params.qaId)
              }
            }
          },
          // タイトル付きPDFのURL(ノート一覧からの遷移)
          {
            path: 'qa-detail-pdf/:noteId/:rowId/:qaId',
            name: 'PCQADetailPdfNote',
            component: () => import(/* webpackChunkName: "pc-qa-detail-pdf" */ '../views/pc/PCQADetailPdf.vue'),
            meta: {
              // title: '★設問タイトル★-Q&A・事例詳細'
              // 設問タイトルは詳細ページ側でつける
              title: '-Q&A・事例詳細PDF'
            },
            // vue-routerがcomposition-apiに対応していないのでパラメータをpropsで渡す
            props: (route) => {
              return {
                noteId: Number(route.params.noteId),
                rowId: Number(route.params.rowId),
                qaId: Number(route.params.qaId)
              }
            }
          },
          // WLJPコンテンツ：新旧対照表（ブラウザの別ウィンドウで表示する）
          {
            path: 'window/qa-old-and-new-mappings-window',
            name: 'PCQAOldAndNewMappingsWindow',
            component: () => import(/* webpackChunkName: "window-pc-qa-old-and-new-mappings-window" */ '../views/pc/window/PCQAOldAndNewMappingsWindow.vue'),
            meta: {
              noAuth: true,
              title: '新旧対照表'
            }
          },
          {
            path: 'knowledge-list',
            name: 'PCKnowledgeList',
            component: () => import(/* webpackChunkName: "pc-knowledge-list" */ '../views/pc/PCKnowledgeList.vue'),
            meta: { title: '最新ナレッジ' }
          },
          {
            path: 'knowledge-detail/:knowledgeId',
            name: 'PCKnowledgeDetail',
            component: () => import(/* webpackChunkName: "pc-knowledge-detail" */ '../views/pc/PCKnowledgeDetail.vue'),
            meta: {
              // title: '★最新ナレッジタイトル★-最新ナレッジ'
              // ナレッジタイトルは詳細ページ側でつける
              title: '-最新ナレッジ'
            },
            // vue-routerがcomposition-apiに対応していないのでパラメータをpropsで渡す
            props: (route) => {
              return {
                knowledgeId: Number(route.params.knowledgeId)
              }
            }
          },
          // 書籍一覧（通常）
          {
            path: 'book-list',
            name: 'PCBookList',
            component: () => import(/* webpackChunkName: "pc-book-list" */ '../views/pc/PCBookList.vue'),
            meta: { title: '書籍一覧' }
          },
          {
            path: 'document-downloads-list',
            name: 'PCDocumentDownloadsList',
            component: () => import(/* webpackChunkName: "pc-document-downloads-list" */ '../views/pc/PCDocumentDownloadsList.vue'),
            meta: { title: '書式ダウンロード' }
          },
          {
            path: 'note-list',
            name: 'PCNoteList',
            component: () => import(/* webpackChunkName: "pc-note-list" */ '../views/pc/PCNoteList.vue'),
            meta: { title: 'ノート' }
          },
          {
            path: 'history-of-view',
            name: 'PCHistoryOfView',
            component: () => import(/* webpackChunkName: "pc-history-of-view" */ '../views/pc/PCHistoryOfView.vue'),
            meta: { title: '閲覧履歴' }
          },
          {
            path: 'history-of-search',
            name: 'PCHistoryOfSearch',
            component: () => import(/* webpackChunkName: "pc-history-of-search" */ '../views/pc/PCHistoryOfSearch.vue'),
            meta: { title: '検索履歴' }
          },
          {
            path: 'alert-list/',
            name: 'PCAlertList',
            component: () => import(/* webpackChunkName: "pc-alert-list" */ '../views/pc/PCAlertList.vue'),
            meta: { title: 'アップデート通知' }
          },
          {
            path: 'search-condition-list',
            name: 'PCSearchConditionList',
            component: () => import(/* webpackChunkName: "pc-search-condition-list" */ '../views/pc/PCSearchConditionList.vue'),
            meta: { title: '保存検索条件' }
          },
          {
            path: 'view-later-list',
            name: 'PCViewLaterList',
            component: () => import(/* webpackChunkName: "pc-view-later-list" */ '../views/pc/PCViewLaterList.vue'),
            meta: { title: '後で見る' }
          },
          {
            path: 'bookmark-list',
            name: 'PCBookmarkList',
            component: () => import(/* webpackChunkName: "pc-bookmark-list" */ '../views/pc/PCBookmarkList.vue'),
            meta: { title: 'ブックマーク' }
          },
          {
            path: 'tag-list',
            name: 'PCTagList',
            component: () => import(/* webpackChunkName: "pc-tag-list" */ '../views/pc/PCTagList.vue'),
            meta: { title: 'タグの管理' }
          },
          {
            path: 'guide/index',
            name: 'PCGuideTop',
            component: () => import(/* webpackChunkName: "pc-guide-detail" */ '../views/pc/PCGuideDetail.vue'),
            meta: {
              title: '-ご利用ガイド'
            },
            // vue-routerがcomposition-apiに対応していないのでパラメータをpropsで渡す
            props: (route) => {
              return {
                guideId: 'index'
              }
            }
          },
          {
            path: 'guide/:guideId',
            name: 'PCGuideDetail',
            component: () => import(/* webpackChunkName: "pc-guide-detail" */ '../views/pc/PCGuideDetail.vue'),
            meta: {
              title: '-ご利用ガイド'
            },
            // vue-routerがcomposition-apiに対応していないのでパラメータをpropsで渡す
            props: (route) => {
              return {
                guideId: route.params.guideId
              }
            }
          },
          {
            path: 'guide-list',
            name: 'PCGuideList',
            component: () => import(/* webpackChunkName: "pc-guide-list" */ '../views/pc/PCGuideList.vue'),
            meta: { title: 'ご利用ガイド' }
          },
          {
            path: 'contract-account/bulk',
            name: 'PCContractAccountBulk',
            component: () => import(/* webpackChunkName: "pc-contract-account" */ '../views/pc/PCContractAccountBulk.vue'),
            meta: {
              requireAdmin: true,
              title: '利用者アカウントの一括登録・更新-管理者メニュー'
            }
          },
          // 印刷機能（新規契約申込画面）
          {
            path: 'new-application-print',
            name: 'PCNewApplicationPrint',
            component: () => import(/* webpackChunkName: "public-new-application-print" */ '../views/pc/LoggedinNewApplicationPrint.vue'),
            meta: {
              requireAdmin: true,
              title: 'お申し込み内容-印刷'
            }
          },
          {
            path: 'inquiry-input',
            name: 'PCInquiryInput',
            component: () => import(/* webpackChunkName: "pc-inquiry-input" */ '../views/pc/PCInquiryInput.vue'),
            meta: { title: 'お問い合わせ-管理者メニュー' }
          },
          {
            path: 'inquiry-confirm',
            name: 'PCInquiryConfirm',
            component: () => import(/* webpackChunkName: "pc-inquiry-confirm" */ '../views/pc/PCInquiryConfirm.vue'),
            meta: {
              // requireAdmin: true, // 管理者以外も利用可能
              title: 'お問い合わせ-管理者メニュー'
            }
          },
          {
            path: 'inquiry-complete',
            name: 'PCInquiryComplete',
            component: () => import(/* webpackChunkName: "pc-inquiry-complete" */ '../views/pc/PCInquiryComplete.vue'),
            meta: {
              // requireAdmin: true,
              title: 'お問い合わせ-管理者メニュー'
            }
          },
          {
            path: 'error',
            name: 'PCError',
            component: () => import(/* webpackChunkName: "pc-error" */ '../views/pc/PCError.vue'),
            meta: { noAuth: true }
          }

        ]
      },
      {
        path: 'sp/',
        name: 'SP',
        component: () => import(/* webpackChunkName: "sp-base" */ '../views/sp/SPBase.vue'),
        children: [
          {
            path: 'sp-module',
            name: 'SPModule',
            component: () => import(/* webpackChunkName: "pc-login" */ '../views/SPModule.vue'),
            meta: {
              title: ''
            }
          },
          {
            path: 'login',
            name: 'SPLogin',
            beforeEnter: (to, from, next) => {
              // 301リダイレクト
              redirectToTechLogin('/lc/app/sp/dashboard')
            },
            // component: () => import(/* webpackChunkName: "sp-login" */ '../views/sp/SPLogin.vue'),
            meta: {
              noAuth: true,
              title: 'ログイン'
            }
          },
          {
            path: 'dashboard',
            name: 'SPDashboard',
            component: () => import(/* webpackChunkName: "sp-dashboard" */ '../views/sp/SPDashboard.vue'),
            meta: { title: 'ダッシュボード' }
          },
          // 資料検索
          {
            path: 'document-list',
            name: 'SPDocumentList',
            component: () => import(/* webpackChunkName: "sp-document-list" */ '../views/sp/SPDocumentList.vue'),
            meta: { title: '資料検索' }
          },
          {
            path: 'document-list/:bookRevisionId',
            name: 'SPDocumentListBook',
            component: () => import(/* webpackChunkName: "sp-document-list-book" */ '../views/sp/SPDocumentList.vue'),
            meta: { title: '資料検索' },
            props: (route) => {
              return {
                bookRevisionId: Number(route.params.bookRevisionId)
              }
            }
          },
          {
            path: 'document-list/theme/:themeId',
            name: 'SPDocumentListTheme',
            component: () => import(/* webpackChunkName: "sp-document-list-theme" */ '../views/sp/SPDocumentList.vue'),
            meta: { title: '資料検索' },
            // vue-routerがcomposition-apiに対応していないのでパラメータをpropsで渡す
            props: (route) => {
              return {
                themeId: Number(route.params.themeId)
              }
            }
          },
          {
            path: 'document-list/detail/:detailId',
            name: 'SPDocumentListDetail',
            component: () => import(/* webpackChunkName: "sp-document-list-detail" */ '../views/sp/SPDocumentList.vue'),
            meta: { title: '資料検索' },
            // vue-routerがcomposition-apiに対応していないのでパラメータをpropsで渡す
            props: (route) => {
              return {
                detailId: Number(route.params.detailId)
              }
            }
          },
          {
            path: 'document-search',
            name: 'SPDocumentSearch',
            component: () => import(/* webpackChunkName: "sp-document-search" */ '../views/sp/SPDocumentSearch.vue'),
            meta: { title: '資料検索' }
          },
          {
            path: 'document-search/detail/:detailId',
            name: 'SPDocumentSearchDetail',
            component: () => import(/* webpackChunkName: "sp-document-search-detail" */ '../views/sp/SPDocumentSearch.vue'),
            meta: { title: '資料検索' },
            // vue-routerがcomposition-apiに対応していないのでパラメータをpropsで渡す
            props: (route) => {
              return {
                detailId: Number(route.params.detailId)
              }
            }
          },
          {
            path: 'information-list',
            name: 'SPInformationList',
            component: () => import(/* webpackChunkName: "sp-information-list" */ '../views/sp/SPInformationList.vue'),
            meta: { title: 'お知らせ一覧' }
          },
          {
            path: 'information-detail/:infoId',
            name: 'SPInformationDetail',
            component: () => import(/* webpackChunkName: "sp-information-detail" */ '../views/sp/SPInformationDetail.vue'),
            // お知らせタイトルは詳細ページ側でつける
            meta: { title: 'お知らせタイトル' },
            // vue-routerがcomposition-apiに対応していないのでパラメータをpropsで渡す
            props: (route) => {
              return {
                infoId: Number(route.params.infoId)
              }
            }
          },
          {
            path: 'qa-list',
            name: 'SPQAList',
            component: () => import(/* webpackChunkName: "sp-qa-list" */ '../views/sp/SPQAList.vue'),
            meta: { title: 'Q&A・事例検索' },
            beforeEnter: async (to, from, next) => {
              // QA一覧に対する遷移元の名前を保存
              store.dispatch('qa/setFromNameForQAList', from.name)
              if (from.name !== 'SPQADetail') {
                // QA詳細画面以外から遷移してきた場合
                // ページ番号を1に初期化する
                store.dispatch('qa/setPage', 1)
              }
              next()
            }
          },
          {
            path: 'qa-detail/:qaId',
            name: 'SPQADetail',
            component: () => import(/* webpackChunkName: "sp-qa-detail" */ '../views/sp/SPQADetail.vue'),
            meta: {
              // title: '★設問タイトル★-Q&A・事例詳細'
              // 設問タイトルは詳細ページ側でつける
              title: '-Q&A・事例詳細'
            },
            // vue-routerがcomposition-apiに対応していないのでパラメータをpropsで渡す
            props: (route) => {
              return {
                qaId: Number(route.params.qaId)
              }
            }
          },
          {
            path: 'qa-table-of-contents',
            name: 'SPQATableOfContents',
            component: () => import(/* webpackChunkName: "sp-qa-table-of-contents" */ '../views/sp/SPQATableOfContents.vue'),
            meta: { title: '目次-Q&A・事例詳細' }
          },
          {
            path: 'knowledge-list',
            name: 'SPKnowledgeList',
            component: () => import(/* webpackChunkName: "sp-knowledge-list" */ '../views/sp/SPKnowledgeList.vue'),
            meta: { title: '最新ナレッジ検索' }
          },
          {
            path: 'knowledge-detail/:knowledgeId',
            name: 'SPKnowledgeDetail',
            component: () => import(/* webpackChunkName: "sp-knowledge-detail" */ '../views/sp/SPKnowledgeDetail.vue'),
            meta: {
              // title: '★最新ナレッジタイトル★-最新ナレッジ'
              // ナレッジタイトルは詳細ページ側でつける
              title: '-最新ナレッジ'
            },
            // vue-routerがcomposition-apiに対応していないのでパラメータをpropsで渡す
            props: (route) => {
              return {
                knowledgeId: Number(route.params.knowledgeId)
              }
            }
          },
          {
            path: 'book-list',
            name: 'SPBookList',
            component: () => import(/* webpackChunkName: "sp-book-list" */ '../views/sp/SPBookList.vue'),
            meta: { title: '書籍一覧' }
          },
          {
            path: 'note-list',
            name: 'SPNoteList',
            component: () => import(/* webpackChunkName: "sp-note-list" */ '../views/sp/SPNoteList.vue'),
            meta: { title: 'ノート' }
          },
          {
            path: 'note-edit/:note_id',
            name: 'SPNoteEdit',
            component: () => import(/* webpackChunkName: "sp-note-edit" */ '../views/sp/SPNoteEdit.vue'),
            meta: {
              // title: '★ノートタイトル★-ノート'
              // ノートタイトルは詳細ページ側でつける
              title: '-ノート'
            },
            // vue-routerがcomposition-apiに対応していないのでパラメータをpropsで渡す
            props: (route) => {
              return {
                noteId: Number(route.params.note_id)
              }
            }
          },
          {
            path: 'history-of-view',
            name: 'SPHistoryOfView',
            component: () => import(/* webpackChunkName: "sp-history-of-view" */ '../views/sp/SPHistoryOfView.vue'),
            meta: { title: '閲覧履歴' }
          },
          {
            path: 'history-of-search',
            name: 'SPHistoryOfSearch',
            component: () => import(/* webpackChunkName: "sp-history-of-search" */ '../views/sp/SPHistoryOfSearch.vue'),
            meta: { title: '検索履歴' }
          },
          {
            path: 'alert-list',
            name: 'SPAlertList',
            component: () => import(/* webpackChunkName: "sp-alert-list" */ '../views/sp/SPAlertList.vue'),
            meta: { title: 'アップデート通知' }
          },
          {
            path: 'search-condition-list',
            name: 'SPSearchConditionList',
            component: () => import(/* webpackChunkName: "sp-search-condition-list" */ '../views/sp/SPSearchConditionList.vue'),
            meta: { title: '保存検索条件' }
          },
          {
            path: 'view-later-list',
            name: 'SPViewLaterList',
            component: () => import(/* webpackChunkName: "sp-view-later-list" */ '../views/sp/SPViewLaterList.vue'),
            meta: { title: '後で見る' }
          },
          {
            path: 'bookmark-list',
            name: 'SPBookmarkList',
            component: () => import(/* webpackChunkName: "sp-bookmark-list" */ '../views/sp/SPBookmarkList.vue'),
            meta: { title: 'ブックマーク' }
          },
          {
            path: 'tag-list',
            name: 'SPTagList',
            component: () => import(/* webpackChunkName: "sp-tag-list" */ '../views/sp/SPTagList.vue'),
            meta: { title: 'タグの管理' }
          },
          {
            path: 'profile',
            name: 'SPProfile',
            component: () => import(/* webpackChunkName: "sp-search-condition-tag-list" */ '../views/sp/SPProfile.vue'),
            meta: { title: 'プロフィール' }
          },
          {
            path: 'guide/index',
            name: 'SPGuideTop',
            component: () => import(/* webpackChunkName: "sp-guide-detail" */ '../views/sp/SPGuideSearchDetail.vue'),
            meta: {
              title: '-ご利用ガイド'
            },
            // vue-routerがcomposition-apiに対応していないのでパラメータをpropsで渡す
            props: (route) => {
              return {
                guideId: 'index'
              }
            }
          },
          {
            path: 'guide/:guideId',
            name: 'SPGuideDetail',
            component: () => import(/* webpackChunkName: "sp-guide-detail" */ '../views/sp/SPGuideSearchDetail.vue'),
            meta: {
              title: '-ご利用ガイド'
            },
            // vue-routerがcomposition-apiに対応していないのでパラメータをpropsで渡す
            props: (route) => {
              return {
                guideId: Number(route.params.guideId)
              }
            }
          },
          {
            path: 'guide-list',
            name: 'SPGuideList',
            component: () => import(/* webpackChunkName: "sp-guide-list" */ '../views/sp/SPGuideList.vue'),
            meta: { title: 'ご利用ガイド' }
          },
          {
            path: 'new-application-input',
            name: 'SPNewApplicationInput',
            component: () => import(/* webpackChunkName: "sp-new-application-input" */ '../views/sp/SPNewApplicationInput.vue'),
            meta: { title: '新規契約申し込み' }
          },
          {
            path: 'new-application-confirm',
            name: 'SPNewApplicationConfirm',
            component: () => import(/* webpackChunkName: "sp-new-application-confirm" */ '../views/sp/SPNewApplicationConfirm.vue'),
            meta: { title: '内容確認-新規契約申し込み' }
          },
          {
            path: 'new-application-complete',
            name: 'SPNewApplicationComplete',
            component: () => import(/* webpackChunkName: "sp-new-application-complete" */ '../views/sp/SPNewApplicationComplete.vue'),
            meta: { title: '完了-新規契約申し込み' }
          },
          {
            path: 'inquiry-input',
            name: 'SPInquiryInput',
            component: () => import(/* webpackChunkName: "sp-inquiry-input" */ '../views/sp/SPInquiryInput.vue'),
            meta: { title: 'お問い合わせ' }
          },
          {
            path: 'inquiry-confirm',
            name: 'SPInquiryConfirm',
            component: () => import(/* webpackChunkName: "sp-inquiry-confirm" */ '../views/sp/SPInquiryConfirm.vue'),
            meta: { title: '確認-お問い合わせ' }
          },
          {
            path: 'inquiry-complete',
            name: 'SPInquiryComplete',
            component: () => import(/* webpackChunkName: "sp-inquiry-complete" */ '../views/sp/SPInquiryComplete.vue'),
            meta: { title: '完了-お問い合わせ' }
          },
          // TODO: 不要ページ削除
          {
            path: 'error',
            name: 'SPError',
            component: () => import(/* webpackChunkName: "sp-error" */ '../views/sp/SPError.vue'),
            meta: { noAuth: true }
          }
        ]
      },
      // Vue本体(/lc/app)パス以外からのエラー表示用（403）
      // APacheのErrorDocument設定で、ここにリダイレクトさせる
      // デバイス判定(PC/SP)して、静的ページにリダイレクトさせる
      {
        path: '/error/403',
        name: '403Page',
        meta: { noAuth: true },
        beforeEnter: async (to, from, next) => {
          const device = judgeDevice()
          const authErrorPage = `/lc/app/error/403-${device}.html`
          window.location = authErrorPage
          next()
        }
      },
      // Vue本体(/lc/app)パス以外からのエラー表示用（403）
      // APacheのErrorDocument設定で、ここにリダイレクトさせる
      // デバイス判定(PC/SP)して、静的ページにリダイレクトさせる
      {
        path: '/error/404',
        name: '404Page',
        meta: { noAuth: true },
        beforeEnter: async (to, from, next) => {
          const device = judgeDevice()
          const authErrorPage = `/lc/app/error/404-${device}.html`
          window.location = authErrorPage
          next()
        }
      },
      // 資料検索用リダイレクト要リンク
      {
        path: 'document-top',
        name: 'DocumentTop',
        component: () => import(/* webpackChunkName: "common-base" */ '../views/CommonBase.vue'),
        beforeEnter: async (to, from, next) => {
          const device = judgeDevice()
          let dotumentUrl = `/lc/app/${device}/`
          if (store.state.precedent.tab === 2) {
            dotumentUrl += 'document-list/'
          } else {
            dotumentUrl += 'document-search/'
          }
          window.location = dotumentUrl
          next()
        }
      }
    ]
  },
  {
    path: '/error/404-pc.html',
    name: '404PCPage',
    meta: { noAuth: true }
  },
  {
    path: '/error/404-sp.html',
    name: '404SPPage',
    meta: { noAuth: true }
  },
  {
    path: '*',
    name: 'NotFound',
    meta: {
      noAuth: true,
      title: ''
    }
  }
]

const router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  routes,
  scrollBehavior (to, from, savedPosition) {
    return { x: 0, y: 0 }
  }
})

// 統合基盤Token確認処理
// to: 遷移先のパス
const checkTechToken = async (to, exchangeToken = null) => {

  console.log('🎨 checkTechToken exchangeToken', exchangeToken)
  
  // localStorageを最新の状態で再取得
  const lcApp = localStorage.getItem('lc-app')
  const lcAppJson = JSON.parse(lcApp)
  const authJson = lcAppJson.auth

  // exchangeTokenがすでに取得されている場合は判別に利用
  const exchangeTokenStorage = localStorage.getItem('lc-exchange-token')

  if (!authJson) {
    redirectToTechLogin(to.path.match('/lc/app/') ? to.path : `/lc/app${to.path}`)
    return
  }

  // exchangeTokenがある場合は統合基盤Token取得APIを呼び出す
  if (
    (exchangeToken && (exchangeTokenStorage && exchangeTokenStorage !== exchangeToken)) ||
    (exchangeToken && !exchangeTokenStorage)
  ) {
    await handleTokenExchange(lcAppJson, to.path, exchangeToken)
  
    return
  }

  // tokenがない場合は統合基盤ログイン画面にリダイレクト
  if (!authJson.token || authJson.token === '') {
    redirectToTechLogin(to.path.match('/lc/app/') ? to.path : `/lc/app${to.path}`)
    return
  }

  // ログイン画面への遷移の場合はダッシュボードにリダイレクト
  if (isLoginPage(to.path)) {
    redirectToDashboard()
  }

  
}

// ログインページかどうかの判定
const isLoginPage = (path) => {
  return path === '/pc/login' || path === '/sp/login'
}

// Token交換処理
const handleTokenExchange = async (storageJson, path, exchangeToken = null) => {
  const params = {
    integrated_system_exchange_token: exchangeToken,
    organization_id: storageJson.profile.organization_id
  }
  
  await apiEx.fetchTokenExchange(params)
    .then(response => {
      const data = response.data

      // localStorage.auth.tokenに統合基盤tokenを格納し、localStorage.auth.exchangeTokenを削除する
      if (data.status === 200) {
        storageJson.auth.token = data.integrated_system_token
        localStorage.setItem('lc-app', JSON.stringify(storageJson))

        // Storeに統合基盤tokenを保存
        store.dispatch('auth/setToken', { token: data.integrated_system_token })
        console.log('🎉 handleTokenExchange fetchTokenExchange success', data.integrated_system_token)

        // 2度Tokenを取得ししないようにLocalStorageにexchangeTokenを保存
        localStorage.setItem('lc-exchange-token', exchangeToken)
      } else {
        const errorCode = data.status
        console.error('‼️ handleTokenExchange fetchTokenExchange error', errorCode, JSON.stringify(params))
        // 401はテックポータルログインへ。それ以外は従来どおりエラー画面へ
        redirectToError(errorCode, path)
      }
    })
    .catch(error => {
      console.error(error)
      // トークン交換API失敗時はテックポータルログインへ誘導する
      // （エンドユーザが遷移しようとしたURLを redirect_url として渡す。クエリのワンタイムトークンは付けない）
      redirectToTechLogin(path)
    })

  
}

// localStorageデータ更新
const updateTechProfile = async () => {
  

  const lcApp = localStorage.getItem('lc-app')
  if (!lcApp) return

  const lcAppJson = JSON.parse(lcApp)
  const profile = lcAppJson.profile

  // プロフィール情報を取得してlocalStorageを更新する共通処理
  const fetchAndUpdateProfile = async (userId, organizationId, lastUpdateTime) => {
    const userDetailRes = await apiEx.fetchUsersDetail()
    const userData = userDetailRes.data

    if (userData.status === 200) {
      await updateTechProfileLocalStorage({
        auth: userData.service?.role_id,
        email: userData.email,
        id: userData.user_id,
        organization_id: organizationId,
        name: userData.account_name,
        last_update_time: lastUpdateTime ?? new Date().toISOString() // TODO: 更新日の取得方法を確認
      })

      // Storeに統合基盤tokenを保存
      store.dispatch('profile/setOrganizationId', organizationId)
      store.dispatch('profile/setUserId', userData.user_id)
      store.dispatch('profile/setLastUpdateTime', lastUpdateTime ?? new Date().toISOString())
    } else {
      // TODO: エラー処理
      const errorCode = userData.status
      console.error('🎨 updateTechProfile fetchUsersDetail error', errorCode)
      redirectToError(errorCode)
    }
  }

  try {
    if (!profile || !profile?.last_update_time || profile?.last_update_time === '') {
      // プロフィールがない、または更新日時が未設定の場合
      const verifyRes = await apiEx.fetchVerify()

      if (verifyRes.data.status === 200) {
        const { lc_user_id: userId, organization_id: organizationId } = verifyRes.data
        await fetchAndUpdateProfile(userId, organizationId)
      } else {
        // TODO: エラー処理
        const errorCode = verifyRes.data.status
        console.error('🎨 updateTechProfile fetchVerify error', errorCode)
        redirectToError(errorCode)
      }
    } else {
      // 更新チェックAPIで更新が必要か確認
      const updateCheckRes = await apiEx.fetchUsersUpdateCheck({
        updated_after: profile.last_update_time
      })

      if (updateCheckRes.data.status === 200 && updateCheckRes.data.is_updated) {
        await fetchAndUpdateProfile(profile.id, profile.organization_id)
      } else {
        // TODO: エラー処理
        const errorCode = updateCheckRes.data.status
        console.error('🎨 updateTechProfile fetchUsersUpdateCheck error', errorCode)
        redirectToError(errorCode)
      }
    }
  } catch (error) {
    // TODO: エラーコードに準じてエラー画面を表示する。
    console.error('🎨 updateTechProfile error', error)
    // redirectToError(error.code) // TODO: 実際のエラーの表示をみてから実装したい
  }

  
}

// exchangeTokenの再設定処理
const ensureExchangeTokenInLocalStorage = (exchangeToken) => {
  
  if (!exchangeToken) return

  const lcApp = localStorage.getItem('lc-app')
  let lcAppJson = null

  if (!lcApp) {
    lcAppJson = {
      auth: {}
    }
  } else {
    lcAppJson = JSON.parse(lcApp)

    if (!lcAppJson.auth) {
      lcAppJson.auth = {}
    }
  }

  
  
  
  lcAppJson.auth.exchangeToken = exchangeToken
  localStorage.setItem('lc-app', JSON.stringify(lcAppJson))
  
}

// organization_idの再設定処理
const ensureOrganizationIdInLocalStorage = (organizationId) => {
  
  if (!organizationId) return

  const lcApp = localStorage.getItem('lc-app')
  let lcAppJson = null

  if (!lcApp) {
    lcAppJson = {
      auth: {}
    }
  } else {
    lcAppJson = JSON.parse(lcApp)

    if (!lcAppJson.profile) {
      lcAppJson.profile = {}
    }
  }

  lcAppJson.profile.organization_id = parseInt(organizationId)
  localStorage.setItem('lc-app', JSON.stringify(lcAppJson))
  
}

const updateTechProfileLocalStorage = async (data = {}) => {
  const lcApp = localStorage.getItem('lc-app')
  if (!lcApp) return

  const lcAppJson = JSON.parse(lcApp)
  lcAppJson.profile = { ...lcAppJson.profile, ...data }
  localStorage.setItem('lc-app', JSON.stringify(lcAppJson))
}

const checkQueryParams = async (to) => {
  // ExchangeTokenの取得
  const exchangeToken = getQueryParam('integrated_system_exchange_token')
  if (exchangeToken) {
    ensureExchangeTokenInLocalStorage(exchangeToken)
    store.dispatch('auth/setExchangeToken', { exchangeToken: exchangeToken })
    
  } else {
    
  }

  // organization_idの取得
  const organizationId = getQueryParam('organization_id')
  if (organizationId) {
    ensureOrganizationIdInLocalStorage(organizationId)
    store.dispatch('profile/setOrganizationId', organizationId)
    
  } else {
    
  }

  return { exchangeToken, organizationId }
}

router.beforeEach(async (to, from, next) => {
  

  const isRequireAuth = !to.meta.noAuth ?? false

  // 初期化
  store.dispatch('common/setDecidedNoticeTitle', '')
  store.dispatch('common/setDecidedNoticeMessage', '')
  if (from.name === 'PCLogin' || from.name === 'SPLogin') {
    // ログイン画面から他の画面に遷移する場合
    // タイムアウト時にログイン画面に遷移した時にNotice表示する判定フラグをリセット
    store.dispatch('login/setIsTimeoutMessageShown', false)
  }

  // 遷移元名、遷移先名をストアに保存
  store.dispatch('common/setFromName', from.name)
  store.dispatch('common/setFromPath', from.path)
  store.dispatch('common/setToName', to.name)
  store.dispatch('common/setToPath', to.path)
  store.dispatch('common/setIsRequireAuth', isRequireAuth)

  // ブラウザタイトル設定処理
  // routerのmetaの中にtitleの記述があればそれをタイトルに設定する
  const siteTitle = 'LEGAL CONNECTION　新日本法規出版の法律情報検索サービス'
  const { title } = to.meta

  // デバイス判定しストアに保存
  store.dispatch('common/setDevice')

  let setTitle
  if (title) {
    setTitle = `${title} | ${siteTitle}`
  } else {
    setTitle = siteTitle
  }
  document.title = setTitle
  document.querySelector("meta[property='og:title']").setAttribute('content', setTitle)
  store.dispatch('common/setTitle', setTitle)

  const token = getQueryParam('token')
  const refreshToken = getQueryParam('refresh_token')

  const { exchangeToken, organizationId } = await checkQueryParams(to)

  // 指定パス外は404へ遷移
  const { common, auth, profile } = store.state
  const device = common.device ?? 'pc'
  const isRequireAdmin = to.meta.requireAdmin ?? false
  const isNoWLJOP = to.meta.noWLJOP ?? false
  const isWLJOP = profile.type === 2 || profile.type === 6

  // iOSのみuser-scaleableを無効にし、inputの強制拡大をむこうに
  if (isSp() && isIos()) {
    const metaLists = document.getElementsByTagName('meta')
    const viewportIndex = _.findIndex(metaLists, meta => meta.getAttribute('name') === 'viewport')
    metaLists[viewportIndex].setAttribute('content', 'width=device-width,initial-scale=1.0,user-scalable=no')
    // setTimeout(() => {
    //   metaLists[viewportIndex].setAttribute('content', 'width=device-width,initial-scale=1.0')
    // }, 100)
  }

  if (to.name === 'NotFound') {
    // routerのパスにヒットしない場合は404ページへ遷移
    const notFoundPage = `/lc/app/error/404-${device}.html`
    window.location = notFoundPage
  } else if ((to.name === 'PCDashboard' || to.name === 'PCQADetail' || to.name === 'PCQADetailPreview' || to.name === 'PCDocumentListPreview') && token !== '') {
    console.log('🎨 to.name is PCDashboard, PCQADetail, PCQADetailPreview, PCDocumentListPreview')
    // 管理システムのフロントエンドログイン / アカウントログイン / 書籍プレビュー / 資料プレビューの場合

    // const isAuthorized = await store.dispatch('auth/checkToken', token, refreshToken)
    // tokenとrefraがパラメータにあるときは無条件でtrueにする

    // if (isAuthorized) {
    // 認証OK

    // クエリ文字列で渡されたトークンとリフレッシュトークンをストアに保存
    store.dispatch('auth/setToken', { token: token, refreshToken: refreshToken })

    //  管理システムからのプレビュー処理時（URL末尾にトークンが付与されていた場合）にPR/001をコール。
    //  レスポンスのlogin_type = 7だった場合
    //    URL末尾に付与されたトークンをローカルストレージ（lc-app.auth.token）に保存し、API呼び出し処理へ。
    //  エラー含むそれ以外の場合
    //    統合基盤Token確認処理に進む。
    await store
      .dispatch('profile/fetch', { token: token })
      .then(async response => {
        const data = response.response.data

        if (data.login_type !== 7) {
          // 統合基盤Token確認処理に進む
          await checkTechToken(to, exchangeToken)
        }
      })
      .catch(async error => {
        // エラー内容をコンソールに出力
        console.error(error)
        // 統合基盤Token確認処理に進む
        ensureExchangeTokenInLocalStorage(exchangeToken)
        ensureOrganizationIdInLocalStorage(organizationId)
        await checkTechToken(to, exchangeToken)
      })

    // 次の画面へ遷移
    next()
  // } else {
    // 認証NG
  //  store.dispatch('common/showErrorInFullScreen', {title: '認証エラー', text: 'このページを表示する権限がありません。'})
  // }
  } else if (isRequireAuth && !auth.token && (from.name !== 'PCLogin' && from.name !== 'SPLogin')) {
    console.log('🎨 isRequireAuth && !auth.token && (from.name !== \'PCLogin\' && from.name !== \'SPLogin\')')
    // 認証が必要なページで認証トークンを保持していない場合

    // 遷移先URIをストアに保存
    store.dispatch('login/setDestinationURI', to.path)

    // ログイン画面へ飛ばす
    // 統合基盤Token確認処理に進む
    await checkTechToken(to, exchangeToken)

    next()
  } else if (isRequireAdmin && isNoWLJOP && isWLJOP) {
    // 管理者権限が必要なページで権限が足りない場合、403へ飛ばす
    const authErrorPage = `/lc/app/error/403-${device}.html`
    window.location = authErrorPage
    next()
  } else if (isRequireAdmin && profile.auth !== 1) {
    // 管理者権限が必要なページで権限が足りない場合、403へ飛ばす
    const authErrorPage = `/lc/app/error/403-${device}.html`
    window.location = authErrorPage
    next()
  } else {
    console.log('🎨 else')
    if (isRequireAuth) {
      // 認証を要求するページの場合
      await checkTechToken(to, exchangeToken)

      // プロフィールがストアに保存されているかチェック
      if (!store.state.profile.id) {
        // プロフィールがストアに保存されていない場合は取得しなおし、ストアに保存する
        // プロフィール取得APIをコール

        await store
          .dispatch('profile/fetch')
          .then((result) => {
            // 利用者アイコンを取得
            store.dispatch('profile/getUserImage')
          })

      }
    }

    if (to.path.match(/^\/(pc|sp)\//)) {
      // アダプティブページの場合

      if (
        (device === 'pc' && to.path.match(/^\/sp\//)) ||
        (device === 'sp' && to.path.match(/^\/pc\//))
      ) {
        // 端末(PC/SP)に対して表示するページ(PC/SP)が正しくない場合は、正しいページを表示する
        const path = `/${device}/` + to.path.replace(/^\/(pc|sp)\//, '')
        next({ path: path })
      } else {
        next()
      }

    } else {
      // レスポンシブページの場合
      next()
    }

  }

  
})

router.afterEach(async (to, from) => {
  // /spは以下のみ実行
  if (to.path.match(/^\/sp\//)) {
    // 強制クラスつけ外し
    document.body.classList.add('force')
    console.log('add class force')
    requestAnimationFrame(() => {
      document.body.classList.remove('force')
      console.log('remove class force')
    })
  }
  // 画面遷移するたびにプロフィール取得APIに通信し、最新の内容に更新
  const isRequireAuth = !to.meta.noAuth ?? false
  if (isRequireAuth) {
    // await checkQueryParams(to)
    // await checkTechToken(to)
    store.dispatch('profile/fetch')
      .then((result) => {
        const axiosResponse = result?.response
        const data = axiosResponse?.data
        if (data?.status === 200) {
          if (data.login_type !== 7) {
            updateTechProfile()
          }
        }
      })
      .catch(error => {
        console.error('🎨 router.afterEach profile/fetch error', error)
      })
    // updateTechProfile()
  }
})



export default router
